/**
 * The sentence a toast or an inline error shows for something a handler caught.
 *
 * Callables throw HttpsError with a message written for people ("This code has
 * ended."), so that is shown as it is, and so is an Error our own code throws
 * with a sentence of its own. Everything else that lands in a catch is worded
 * for developers: a callable that crashed reaches the browser as the bare code
 * ("internal"), the Firebase SDKs word theirs as "Firebase: Error
 * (auth/popup-blocked)." or "Missing or insufficient permissions.", and a
 * dropped connection reads "Failed to fetch". Those give way to the caller's
 * fallback, to CONNECTION_ERROR_MESSAGE when the network failed, or to one of
 * the AUTH_MESSAGES below when the person has something specific to do.
 *
 * src/lib/review-action-errors.ts is the review dialog's own version of this
 * rule, with review-specific wording.
 */

export const CONNECTION_ERROR_MESSAGE = "We couldn't reach supa.place. Check your connection and try again.";

/**
 * Firebase Auth failures a person can act on. Any other auth code gets the
 * caller's fallback: its message is the SDK's.
 */
const AUTH_MESSAGES: Record<string, string> = {
  'auth/too-many-requests': 'Too many attempts. Wait a few minutes, then try again.',
  'auth/popup-blocked': 'Your browser blocked the login window. Allow pop-ups for supa.place, then try again.',
  'auth/popup-closed-by-user': 'The login window closed before it finished. Try again.',
  'auth/cancelled-popup-request': 'The login window closed before it finished. Try again.',
  'auth/account-exists-with-different-credential':
    'This email already has an account with a different login method. Log in that way, then link this one in Account settings.',
  'auth/credential-already-in-use': 'That account is already linked to another supa.place account.',
  'auth/provider-already-linked': 'That login method is already linked to your account.',
  'auth/no-such-provider': "That login method isn't linked to your account.",
  'auth/email-already-in-use': "There's already an account with this email. Log in instead.",
  'auth/invalid-email': "That email address doesn't look right.",
  'auth/weak-password': 'Choose a stronger password.',
  'auth/requires-recent-login': 'For your security, log in again, then try again.',
  'auth/wrong-password': "That password isn't right. Try again.",
  'auth/invalid-credential': "That password isn't right. Try again.",
  'auth/invalid-login-credentials': "That password isn't right. Try again.",
  'auth/user-not-found': "We couldn't find that account.",
  'auth/user-disabled': 'This account has been disabled. Contact support.',
  'auth/invalid-verification-code': "That code isn't right, or it has expired. Check it and try again.",
  'auth/code-expired': 'That code has expired. Ask for a new one.',
  'auth/expired-action-code': 'This link has expired. Ask for a new one.',
  'auth/invalid-action-code': 'This link has expired or has already been used. Ask for a new one.',
};

/** Codes that mean the request never got an answer. */
const NETWORK_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'auth/network-request-failed',
  'storage/retry-limit-exceeded',
]);

const NETWORK_WORDING = /^(Failed to fetch|Load failed|NetworkError|Network request failed|The network connection was lost)/i;

/** Wording that is a runtime's or an SDK's, never copy, even on an Error with no code. */
const DEVELOPER_WORDING = [
  /^Firebase/i,
  /\((auth|storage|firestore|functions|app-check|installations|messaging)\/[a-z-]+\)/i,
  /Missing or insufficient permissions/i,
  /\b(undefined|null) is not\b|Cannot read propert|is not a function|is not defined|Unexpected token|in JSON at position/i,
  /\[object Object\]/,
];

/** Errors thrown by the runtime rather than by our code with a sentence. */
const RUNTIME_ERROR_NAMES = new Set(['TypeError', 'SyntaxError', 'RangeError', 'ReferenceError', 'EvalError', 'URIError']);

function field(err: unknown, key: 'code' | 'message' | 'name'): string {
  if (!err || typeof err !== 'object') return '';
  const value = (err as Record<string, unknown>)[key];
  return typeof value === 'string' ? value.trim() : '';
}

/** A callable's message that is only its code ("internal", "INTERNAL"), not a sentence. */
function isBareCode(message: string, code: string): boolean {
  const m = message.toLowerCase().replace(/_/g, '-');
  return m === code || m === 'internal' || m === 'unknown';
}

/** The sentence to show for `err`, or `fallback` when its own wording isn't meant for people. */
export function userErrorMessage(err: unknown, fallback: string): string {
  const code = field(err, 'code');
  // Firebase 12.18 and later end every callable message in " [<HTTP status>]".
  const message = field(err, 'message').replace(/\s*\[\d{3}\]$/, '');

  if (code.startsWith('functions/')) {
    const fnCode = code.slice('functions/'.length);
    // A callable writes its own sentence even for these codes ("We couldn't
    // open a new verification…"); only a bare code means nothing was said.
    if (!message || isBareCode(message, fnCode)) {
      return NETWORK_CODES.has(fnCode) ? CONNECTION_ERROR_MESSAGE : fallback;
    }
    if (NETWORK_WORDING.test(message)) return CONNECTION_ERROR_MESSAGE;
    return DEVELOPER_WORDING.some((re) => re.test(message)) ? fallback : message;
  }

  if (code) {
    // Firebase Auth, Storage and Firestore errors: the message is the SDK's.
    if (NETWORK_CODES.has(code)) return CONNECTION_ERROR_MESSAGE;
    return AUTH_MESSAGES[code] ?? fallback;
  }

  if (!message) return fallback;
  if (NETWORK_WORDING.test(message)) return CONNECTION_ERROR_MESSAGE;
  if (RUNTIME_ERROR_NAMES.has(field(err, 'name'))) return fallback;
  if (DEVELOPER_WORDING.some((re) => re.test(message))) return fallback;
  return message;
}

/**
 * A failed save on a form that keeps what was typed (`kept`, e.g. "Your
 * changes are still here."): the reason first when there is one people can act
 * on, else the reassurance and a retry.
 */
export function savedFormFailure(err: unknown, kept: string): string {
  const reason = userErrorMessage(err, '');
  return reason ? `${reason} ${kept}` : `${kept} Try again in a moment.`;
}

/**
 * Stripe.js errors. Stripe writes card and validation errors for the payer
 * ("Your card was declined."); every other type is meant for us, so the payer
 * gets `fallback`.
 */
export function paymentErrorMessage(
  error: { type?: unknown; message?: unknown } | null | undefined,
  fallback: string,
): string {
  if (!error) return fallback;
  const message = typeof error.message === 'string' ? error.message.trim() : '';
  if (message && (error.type === 'card_error' || error.type === 'validation_error')) return message;
  return fallback;
}
